import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ReRunAllocation } from '../libs/GraphQL';
import ConfirmationModal from '../organisms/ConfirmationModal/ConfirmationModal';

const RunAllocation = () => {
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowSuccessModal, setIsShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [packingSlipGroup, setPackingSlipGroup] = useState('MW');
  const [isRunBlocked, setIsRunBlocked] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    const blockForMinutes = 15;

    const updateBlockStatus = () => {
      const lastReRunTimestamp = localStorage.getItem(
        'allocationReRunTimestamp',
      );

      if (lastReRunTimestamp) {
        const currentTime = moment().valueOf();
        const elapsedTime = currentTime - lastReRunTimestamp;
        const blockDuration = blockForMinutes * 60 * 1000;
        const remainingTime = blockDuration - elapsedTime;

        if (remainingTime > 0) {
          setIsRunBlocked(true);
          setTimeRemaining(remainingTime);
        } else {
          setIsRunBlocked(false);
          setTimeRemaining(null);
        }
      } else {
        setIsRunBlocked(false);
        setTimeRemaining(null);
      }
    };

    updateBlockStatus();
    const intervalId = setInterval(updateBlockStatus, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const formatTimeRemaining = (milliseconds) => {
    const duration = moment.duration(milliseconds);
    const minutes = Math.floor(duration.asMinutes());
    const seconds = duration.seconds();
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleCloseSuccessModal = () => {
    setIsShowSuccessModal(false);
    setIsRunBlocked(true);
  };

  const handleRunAllocationClick = async () => {
    setIsShowModal(false);
    setIsLoading(true);

    // Set timestamp IMMEDIATELY to prevent duplicate calls (even on page reload)
    const currentTime = moment().valueOf();
    localStorage.setItem('allocationReRunTimestamp', currentTime);
    setIsRunBlocked(true);
    setTimeRemaining(15 * 60 * 1000);

    try {
      await ReRunAllocation(packingSlipGroup);
      // Success - timestamp already set, just show success modal
      setIsLoading(false);
      setIsShowSuccessModal(true);
    } catch (error) {
      console.log(error);
      setIsLoading(false);

      if (error.errors && error.errors[0].errorType === 'ExecutionTimeout') {
        // ExecutionTimeout is non-blocking but allocation likely started
        // Keep the timestamp and show success
        console.log('AppSync time out - this is a non-blocking error');
        setIsShowSuccessModal(true);
      } else {
        // Real error - CLEAR the timestamp to allow retry
        localStorage.removeItem('allocationReRunTimestamp');
        setIsRunBlocked(false);
        setTimeRemaining(null);
        toast.error(
          'We had issues re-running allocation. Please contact the on-call engineer.',
        );
      }
    }
  };
  return (
    <div style={{ textAlign: 'center', marginTop: '50px' }}>
      <ConfirmationModal
        title="Re-Run Allocation Confirmation"
        show={isShowModal}
        onHide={() => setIsShowModal(false)}
        onConfirm={handleRunAllocationClick}
        loading={isLoading}
      >
        <div>
          <p>
            Are you sure you want to re-run allocation for the{' '}
            <strong>MW</strong> region?
          </p>
          <p style={{ marginTop: '10px', color: '#856404', fontSize: '14px' }}>
            This process can take up to 15 minutes to complete.
          </p>
        </div>
      </ConfirmationModal>

      <Modal
        show={isShowSuccessModal}
        onHide={handleCloseSuccessModal}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header
          style={{
            backgroundColor: '#d4edda',
            borderBottom: '1px solid #c3e6cb',
          }}
        >
          <Modal.Title style={{ color: '#155724', fontWeight: 'bold' }}>
            ✅ Allocation Re-Run Started
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#d4edda', padding: '20px' }}>
          <p
            style={{
              fontSize: '16px',
              marginBottom: '15px',
              color: '#155724',
              fontWeight: '500',
            }}
          >
            Re-run of allocation has been started successfully!
          </p>
          <p style={{ color: '#155724', marginBottom: '0' }}>
            This process can take up to 15 minutes to complete. Please check the
            allocation email to confirm a successful re-run.
          </p>
        </Modal.Body>
        <Modal.Footer
          style={{ backgroundColor: '#d4edda', borderTop: '1px solid #c3e6cb' }}
        >
          <Button
            onClick={handleCloseSuccessModal}
            style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <h3>Fridge Allocation</h3>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '15px',
        }}
      >
        <Button
          onClick={() => setIsShowModal(true)}
          disabled={isLoading || isRunBlocked || isShowSuccessModal}
          style={{ minWidth: '200px' }}
        >
          {isLoading ? 'Loading...' : 'Re-Run Allocation'}
        </Button>
        {isRunBlocked && timeRemaining && (
          <div
            style={{
              padding: '12px 20px',
              backgroundColor: '#fff3cd',
              color: '#856404',
              border: '1px solid #ffeaa7',
              borderRadius: '6px',
              maxWidth: '400px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            <div style={{ marginBottom: '5px' }}>
              <strong>⏱️ Re-run allocation is temporarily blocked</strong>
            </div>
            <div>
              Available in:{' '}
              <strong style={{ fontSize: '16px', color: '#d39e00' }}>
                {formatTimeRemaining(timeRemaining)}
              </strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RunAllocation;
